import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { KeyStatType, responseFilterActiveColor, responseFilterBaselineColor, responseFilterBaselineTextColor } from '../../constants'
import { type ICalculation } from '../../../../../models/Report'
import ReportsChip from '../../ReportsChip'
import { black, shade } from '../../../../../colors'
import { filterVisibleCalculations, hasDisplayValue } from '../../helper'
import { type VisibleKeyStats } from '../../../types'

interface StatisticBoxProps {
  calculations: ICalculation[] | null | undefined
  comparisonCalculations?: ICalculation[] | null
  visibleKeyStats: VisibleKeyStats
}

const statTypes = [
  KeyStatType.STANDARD_DEVIATION,
  KeyStatType.MEDIAN,
  KeyStatType.MODE
]

export const StatisticBox: React.FC<StatisticBoxProps> = ({ calculations, comparisonCalculations = null, visibleKeyStats }) => {
  const theme = useTheme()

  const wrapperStyle = {
    padding: theme.spacing(4),
    borderTop: `1px solid ${shade[650]}`,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: {
      xs: 'space-evenly'
    },
    alignItems: 'center',
    color: theme.palette.text.secondary,
    mt: theme.spacing(6),
    gap: 1
  }

  // Filter and sort the statistics based on the desired order
  const showStats = filterVisibleCalculations(calculations, visibleKeyStats)
    ?.filter((keyStatistic) => statTypes.includes(keyStatistic.name as KeyStatType))
    ?.sort((a, b) => statTypes.indexOf(a.name as KeyStatType) - statTypes.indexOf(b.name as KeyStatType))
  const showComparisonStats = filterVisibleCalculations(comparisonCalculations, visibleKeyStats)
    ?.filter((keyStatistic) => statTypes.includes(keyStatistic.name as KeyStatType))
    ?.sort((a, b) => statTypes.indexOf(a.name as KeyStatType) - statTypes.indexOf(b.name as KeyStatType))
  const comparisonStatsByName = new Map((showComparisonStats ?? []).map((stat) => [stat.name, stat]))

  if (!showStats || showStats.length === 0) {
    return null
  }

  return (
    <Box sx={wrapperStyle}>
      {showStats?.map(({ name, values }) => (
        <Box
          key={name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2)
          }}
        >
          <Typography fontWeight={600} color={black[100]}>
            {name}:
          </Typography>
          {comparisonStatsByName.has(name)
            ? (
              <React.Fragment>
                <ReportsChip
                  index={0}
                  label={hasDisplayValue(comparisonStatsByName.get(name)?.values?.[0]) ? comparisonStatsByName.get(name)?.values?.[0] as string | number : 'N/A'}
                  backgroundColor={responseFilterBaselineColor}
                  textColor={responseFilterBaselineTextColor}
                />
                <ReportsChip
                  index={1}
                  label={hasDisplayValue(values?.[0]) ? values?.[0] as string | number : 'N/A'}
                  backgroundColor={responseFilterActiveColor}
                />
              </React.Fragment>
              )
            : values?.map((value, index) => (
              <ReportsChip
                key={index}
                index={index}
                label={hasDisplayValue(value) ? value : 'N/A'}
              />
            ))}
        </Box>
      ))}
    </Box>
  )
}

export default StatisticBox
